import {doc} from '../docs.js';
import {complex} from './complex/complex.js';
import {gl_functional} from './sdf/functional.js';
import {sdf_groups} from './sdf/group.js';
import {gl_mat3} from './matrix.js';
import {sdfBuilder, sdfManager, rayMarchingManager} from './sdf/sdf-manager.js';
import {raymarching} from './sdf/raymarching.js';
import {gl_unpack} from './unpack.js';
import {colors} from './colors.js';
import {math} from './math.js';
import {gl_uniform, gl_variable} from './variables.js';
import {gl_texture2D} from './texture.js';
import {hashCode} from "utils/hashCode.js"
import {lazy} from './lazy.js';
import {extractCode} from './extract.js';
import {conditions} from './cond.js';
import {sdf} from './sdf/sdf.js';
import {gl_sdf_copy, gl_sdf_multi_copies, gl_sdf_cube_layout} from './sdf/copy.js';
import {vec} from './vec.js';
import { float, utils, extract} from './utils.js';
import {gl_fog} from './fog.js';
import {lighting} from './sdf/light.js';
import {gl} from './struct.js';

doc(
    "gl.+",
    {
        "x": "x",
        "y": "y",
    },
    "Creates GLSL expression for addition",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 2,
    });

export const gl_plus  = (object, b) => {
    object.needsHotReload = true;
    return lazy((a) => {
        let codes = extractCode(a,b);
        object.flash();
        return `(${float(codes[0])}) + (${float(codes[1])})`;
    }, b);
};

doc(
    "gl.-",
    {
        "x": "x",
        "y": "y",
    },
    "Creates GLSL expression for subtraction",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 2,
    });

const gl_minus = (object, b) => {
    object.needsHotReload = true;
    return lazy((a) => {
        let codes = extractCode(a,b);
        object.flash();
        return `(${float(codes[0])}) - (${float(codes[1])})`;
    }, b);
};

doc(
    "gl.*",
    {
        "x":"",
        "y":"",
    },
    "Creates GLSL expression for multiplication",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 2,
    });

export const gl_times = (object, b) => {
    object.needsHotReload = true;
    return lazy((a) => {
        let codes = extractCode(a,b);
        object.flash();
        return `(${float(codes[0])}) * (${float(codes[1])})`;
    }, b);
};


doc(
    "gl./",
    {
    },
    "Fuck",
    () => 1,
    {
        isHot: true,
        inlets: 2,
    });

const gl_divide = (object, b) => {
    object.needsHotReload = true;
    object.needsHotReload = true;
    return lazy((a) => {
        let codes = extractCode(a,b);
        object.flash();
        return `(${float(codes[0])}) / (${float(codes[1])})`;
    }, b);
};

doc(
    "gl.dot",
    {
        "x": "x",
        "y": "y",
    },
    "Creates GLSL expression for dot product of vectors",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 2,
    });

export const gl_dot = (object, b) => {
    object.needsHotReload = true;
    return lazy((a) => {
        let codes = extractCode(a,b);
        object.flash();
        return `(dot((${float(codes[0])}), (${float(codes[1])})))`;
    }, b);
};

doc(
    "gl.resolution",
    {
    },
    "Creates GLSL expression for the resolution vec2",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    }
);
const gl_resolution = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;

    let obj = {
        code: "vec2(width, height)",
        hashCode: "vec2(width, height)",
        variables: {},
        uniforms: {}
    };
    return () => {
        return obj;
    };
};

doc(
    "gl.xy",
    {
    },
    "Creates GLSL expression for the current pixel/'s x & y coordinates as a vec2",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    }
);
const gl_xy = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;

    let obj = {
        code: "gl_FragCoord.xy",
        hashCode: "gl_FragCoord.xy",
        variables: {},
        uniforms: {}
    };
    return () => {
        return obj;
    };
};

doc(
    "gl.bad_uv",
    {
    },
    "Creates GLSL expression for the current pixel/'s x & y coordinates as a vec2",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    }
);

const gl_bad_uv = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;

    let obj = {
        code: "bad_uv", //"(1.0*gl_FragCoord.xy-vec2(width, height))/height",
        hashCode: hashCode("(1.0*gl_FragCoord.xy-vec2(width, height))/height"),
        variables: {
            bad_uv: {
                code: "(1.0*gl_FragCoord.xy-vec2(width, height))/height",
                type: "vec2"
            }
        },
        uniforms: {}
    };
    return () => {
        return obj;
    };
};

doc(
    "gl.uv",
    {
    },
    "Creates GLSL expression for the current pixel/'s x & y coordinates as a vec2",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    }
);

const gl_uv = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;

    let obj = {
        code: "uv_b", //"((1.0*gl_FragCoord.xy-vec2(width, height))/vec2(width, height))",
        hashCode: hashCode("((1.0*gl_FragCoord.xy-vec2(width, height))/vec(width, height))"),
        variables: {
            "uv_b": {
                code: "((1.0*gl_FragCoord.xy-vec2(width, height))/vec2(width, height))",
                type: "vec2",
            }
                
        },
        uniforms: {}
    };
    return () => {
        return obj;
    };
};


doc(
    "gl.x",
    {
    },
    "Creates GLSL expression for the current pixel/'s x-coordinate",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    }
);
const gl_x = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;

    let obj = {
        code: "gl_FragCoord.x",
        hashCode: "gl_FragCoord.x",
        variables: {},
        uniforms: {}
    };
    return () => {
        return obj;
    };
};

doc(
    "gl.y",
    {
    },
    "Creates an GLSL expression for the current pixel/'s y-coordinate",
    () => 1,
    {
        inlets: 0,
        tags: "GLSL"
    });

const gl_y = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;
    let obj = {
        code: "gl_FragCoord.y",
        hashCode: hashCode("gl_FragCoord.y"),
        variables: {},
        uniforms: {}
    };
    return () => {
        object.flash();
        return obj;
    };
};

doc(
    "gl.fragColor",
    {
        "color (vec4)": ""
    },
    "Outputs the GLSL vec4 in the inlet to the shader. Represents the final output for the shader",
    () => 1, {
        inlets: 1,
        tags: "GLSL",
        connects: {
            object: "gl.material",
        },
        
    });

const gl_fragColor = (object) => {
    object.needsHotReload = true;
    return ([a]) => {
        let code = a.code || a;
        let uniforms = a.uniforms;
        let variables = a.variables;
        let functions = a.functions;
        object.flash();
        return gl("gl_FragColor = " + code + ";", a.uniforms, variables, functions);
    };
};

doc(
    "gl.width",
    {
    },
    "Creates GLSL expression for the canvas/'s width",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 0,
    });

const gl_width  = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;
    let obj = {
        code: "width",
        hashCode: "width"
    };
    return ([a]) => {
        object.flash();
        return obj;
    };
};

doc(
    "gl.height",
    {
    },
    "Creates GLSL expression for the canvas/'s height",
    () => 1,
    {
        tags: "GLSL",
        isHot: true,
        inlets: 0,
    });

const gl_height  = (object) => {
    object.needsLoad = true;
    object.needsHotReload = true;
    let obj = {
        code: "height",
        hashCode: hashCode("height"),
    };
    return ([a]) => {
        object.flash();
        return obj;
    };
};

export const gl_index = {
    "gl.uniform": gl_uniform,
    "gl.variable": gl_variable,
    "gl.fragColor": gl_fragColor,
    "gl.x": gl_x,
    "gl.y": gl_y,
    "gl.+": gl_plus,
    "gl.-": gl_minus,
    "gl.*": gl_times,
    "gl./": gl_divide,
    "gl.dot": gl_dot,
    "gl.xy": gl_xy,
    "gl.resolution": gl_resolution,
    "gl.width": gl_width,
    "gl.height": gl_height,
    "gl.texture2D": gl_texture2D,
    ... utils,
    ... conditions,
    ...sdf,
    ...vec,
    ...math,
    ...colors,
    "gl.unpack": gl_unpack,
    "gl.bad_uv": gl_bad_uv,
    ... raymarching,
    "gl.sdfManager": sdfManager,
    "sdf.manager": sdfManager,
    "gl.sdfBuilder": sdfBuilder,
    "sdf.builder": sdfBuilder,
    "gl.rayMarchingManager": rayMarchingManager,
    "raymarching.manager": rayMarchingManager,
    "gl.mat3": gl_mat3,
    "gl.sdfCopy": gl_sdf_copy,
    "sdf.copy": gl_sdf_copy,
    "gl.sdfMultiCopies": gl_sdf_multi_copies,
    "sdf.multiCopies": gl_sdf_multi_copies,
    "gl.sdfCubeLayout": gl_sdf_cube_layout,
    "sdf.cubeLayout": gl_sdf_cube_layout,
    "gl.uv": gl_uv,
    ... sdf_groups,
    ... gl_functional,
    "gl.fog" : gl_fog,
    ... lighting,
    ... complex
};
